import React, { useState, useEffect } from 'react';
import { 
    TextField, Button, Container, Paper, Box, Typography, 
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Stack, useMediaQuery, useTheme, Grid 
} from '@mui/material';
import { GiTennisCourt } from "react-icons/gi";
import axios from 'axios';
import { API_BASE_URL } from '../../App.js';
import { toast } from 'react-toastify';
import ConfirmModal from '../../components/ConfirmModal';

const Courts = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [courts, setCourts] = useState([]);
    const [confirmConfig, setConfirmConfig] = useState({
        open: false,
        title: '',
        message: '',
        onConfirm: () => {},
        type: 'question',
        confirmText: 'Bestätigen'
    });

    useEffect(() => {
        fetchCourts();
    }, []);

    const fetchCourts = async () => {
        try {
            const res = await axios.get(`${API_BASE_URL}/api/courts`);
            setCourts(res.data);
        } catch (error) {
            toast.error("Fehler beim Laden der Courts");
        }
    };

    const handleToggleStatus = (id, currentStatus) => {
        const action = currentStatus ? 'deaktivieren' : 'aktivieren';
        setConfirmConfig({
            open: true,
            title: `Platz ${action}?`,
            message: `Möchtest du diesen Platz wirklich ${action}? ${currentStatus ? 'Der Platz kann dann nicht mehr gebucht werden.' : 'Der Platz steht dann wieder für Buchungen zur Verfügung.'}`,
            type: currentStatus ? 'warning' : 'info',
            confirmText: currentStatus ? 'Deaktivieren' : 'Aktivieren',
            onConfirm: async () => {
                try {
                    await axios.patch(`${API_BASE_URL}/api/courts/${id}/status`, { isActive: !currentStatus });
                    toast.success(currentStatus ? "Platz deaktiviert" : "Platz aktiviert");
                    fetchCourts();
                } catch (error) {
                    toast.error("Fehler beim Ändern des Status");
                }
            }
        });
    };

    const handleUpdateCourt = async (id, data) => {
        try {
            await axios.put(`${API_BASE_URL}/api/courts/${id}`, data);
            toast.success("Platz aktualisiert");
            fetchCourts();
        } catch (error) {
            toast.error("Fehler beim Aktualisieren des Platzes");
        }
    };

    return (
        <Container maxWidth={false} disableGutters>
            <Paper sx={{ p: { xs: 2, md: 3 }, mb: 4 }}>
                <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h5" component="h1" color="primary">
                        Platzverwaltung
                    </Typography>
                </Box>



                {/* Liste der Courts - Responsive: Karten auf Mobil, Tabelle auf Desktop */}
                {isMobile ? (
                    <Stack spacing={2}>
                        {courts.map((court) => (
                            <Paper 
                                key={court._id} 
                                variant="outlined" 
                                sx={{ p: 2, borderRadius: 2, bgcolor: court.isActive ? 'rgba(46, 125, 50, 0.02)' : 'rgba(237, 108, 2, 0.02)' }}
                            >
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <GiTennisCourt className={court.isActive ? 'text-success' : 'text-muted'} size={24} />
                                        <Typography variant="subtitle1" fontWeight="bold">Platz {court.courtNumber}</Typography>
                                    </Box>
                                </Box>

                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <Typography variant="caption" color="text.secondary" fontWeight="bold">VERFÜGBAR VON</Typography>
                                        <TextField
                                            type="date"
                                            size="small"
                                            fullWidth
                                            value={court.availableFrom ? court.availableFrom.split('T')[0] : ''}
                                            onChange={(e) => handleUpdateCourt(court._id, { availableFrom: e.target.value || null })}
                                            InputLabelProps={{ shrink: true }}
                                            sx={{ mt: 0.5 }}
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="caption" color="text.secondary" fontWeight="bold">VERFÜGBAR BIS</Typography>
                                        <TextField
                                            type="date"
                                            size="small"
                                            fullWidth
                                            value={court.availableUntil ? court.availableUntil.split('T')[0] : ''}
                                            onChange={(e) => handleUpdateCourt(court._id, { availableUntil: e.target.value || null })}
                                            InputLabelProps={{ shrink: true }}
                                            sx={{ mt: 0.5 }}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Button 
                                            fullWidth
                                            variant={court.isActive ? "outlined" : "contained"}
                                            color={court.isActive ? "success" : "warning"}
                                            onClick={() => handleToggleStatus(court._id, court.isActive)}
                                            sx={{ mt: 1 }}
                                        >
                                            {court.isActive ? "Platz ist Aktiv" : "Platz ist Inaktiv"}
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Paper>
                        ))}
                    </Stack>
                ) : (
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Nr.</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', display: { xs: 'none', md: 'table-cell' } }}>Name</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Verfügbar von</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Verfügbar bis</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Status</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {courts.map((court) => (
                                    <TableRow key={court._id} hover>
                                        <TableCell align="center" className="fs-7">
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                                                <GiTennisCourt className={court.isActive ? 'text-success' : 'text-muted'} /> 
                                                {court.courtNumber}
                                            </Box>
                                        </TableCell>
                                        <TableCell className="fs-7" sx={{ display: { xs: 'none', md: 'table-cell' } }}>{court.name}</TableCell>
                                        
                                        <TableCell align="center">
                                            <TextField
                                                type="date"
                                                size="small"
                                                value={court.availableFrom ? court.availableFrom.split('T')[0] : ''}
                                                onChange={(e) => handleUpdateCourt(court._id, { availableFrom: e.target.value || null })}
                                                InputLabelProps={{ shrink: true }}
                                                sx={{ width: 150 }}
                                            />
                                        </TableCell>
                                        
                                        <TableCell align="center">
                                            <TextField
                                                type="date"
                                                size="small"
                                                value={court.availableUntil ? court.availableUntil.split('T')[0] : ''}
                                                onChange={(e) => handleUpdateCourt(court._id, { availableUntil: e.target.value || null })}
                                                InputLabelProps={{ shrink: true }}
                                                sx={{ width: 150 }}
                                            />
                                        </TableCell>

                                        <TableCell align="center" className="fs-7">
                                            <Button 
                                                size="small" 
                                                variant={court.isActive ? "outlined" : "contained"}
                                                color={court.isActive ? "success" : "warning"}
                                                onClick={() => handleToggleStatus(court._id, court.isActive)}
                                            >
                                                {court.isActive ? "Aktiv" : "Inaktiv"}
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
                {courts.length === 0 && (
                    <Box sx={{ py: 5, textAlign: 'center' }}>
                         <Typography color="text.secondary">Keine Plätze vorhanden.</Typography>
                    </Box>
                )}
            </Paper>

            {/* Bestätigungs-Dialog */}
            <ConfirmModal
                open={confirmConfig.open}
                onClose={() => setConfirmConfig({ ...confirmConfig, open: false })}
                onConfirm={confirmConfig.onConfirm}
                title={confirmConfig.title}
                message={confirmConfig.message}
                type={confirmConfig.type}
                confirmText={confirmConfig.confirmText}
            />
        </Container>
    );
};

export default Courts;
